import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { getLeaderboardData, getDepartments, getRoles, setLeaderboardOverride, removeLeaderboardOverride, getAvatarDisplayUrl } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  Trophy,
  Medal,
  Award,
  Star,
  TrendingUp,
  Phone,
  Clock,
  Users,
  Building2,
  MapPin,
  Filter,
  X,
  MoreVertical,
  Ban,
  MessageSquare,
  CheckCircle,
  Crown
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = [
  { value: 'overall', label: 'Overall Score', icon: Star, color: 'text-yellow-500' },
  { value: 'communication', label: 'Communication', icon: MessageSquare, color: 'text-blue-500' },
  { value: 'professionalism', label: 'Professionalism', icon: Award, color: 'text-purple-500' },
  { value: 'call_volume', label: 'Call Volume', icon: Phone, color: 'text-green-500' },
  { value: 'improvement_rate', label: 'Improvement Rate', icon: TrendingUp, color: 'text-emerald-500' },
  { value: 'listening', label: 'Listening Skills', icon: Users, color: 'text-indigo-500' },
  { value: 'engagement', label: 'Engagement', icon: CheckCircle, color: 'text-pink-500' },
];

// Generate month options
const getMonthOptions = () => {
  const months = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      value: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
      label: date.toLocaleString('default', { month: 'long', year: 'numeric' })
    });
  }
  return months;
};

const MONTH_OPTIONS = getMonthOptions();

const CALL_TYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'introduction', label: 'Introduction' },
  { value: 'screening', label: 'Screening' },
  { value: 'interview', label: 'Interview' },
  { value: 'followup', label: 'Follow-up' },
  { value: 'client', label: 'Client' },
];

export default function AdminLeaderboard() {
  // Handle both legacy admin and SaaS tenant contexts
  const outletContext = useOutletContext() || {};
  const adminUrl = outletContext.adminUrl;
  const [activeCategory, setActiveCategory] = useState('overall');
  const [leaderboard, setLeaderboard] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 50;
  
  // Filters
  const [filters, setFilters] = useState({
    department_id: '',
    role_id: '',
    location: '',
    call_type: '',
    month: '',
  });
  
  // Override dialog
  const [overrideDialog, setOverrideDialog] = useState(null);
  const [overrideNote, setOverrideNote] = useState('');

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [deptRes, roleRes] = await Promise.all([
          getDepartments(),
          getRoles()
        ]);
        setDepartments(deptRes.data);
        setRoles(roleRes.data);
      } catch (error) {
        console.error('Failed to fetch initial data:', error);
      }
    };
    fetchInitialData();
  }, []);

  // Reset page when filters or category changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeCategory, filters]);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      setIsLoading(true);
      try {
        const params = { 
          category: activeCategory, 
          limit: PAGE_SIZE,
          skip: (currentPage - 1) * PAGE_SIZE
        };
        if (filters.department_id) params.department_id = filters.department_id;
        if (filters.role_id) params.role_id = filters.role_id;
        if (filters.location) params.location = filters.location;
        if (filters.call_type) params.call_type = filters.call_type;
        if (filters.month) params.month = filters.month;
        
        const { data } = await getLeaderboardData(params);
        setLeaderboard(data);
      } catch (error) {
        console.error('Failed to fetch leaderboard:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchLeaderboard();
  }, [activeCategory, filters, currentPage]);

  const handleOverride = async (action) => {
    if (!overrideDialog) return;
    
    try {
      await setLeaderboardOverride(overrideDialog.user_id, activeCategory, action, overrideNote);
      toast.success(`User ${action === 'exclude' ? 'excluded from' : 'marked in'} leaderboard`);
      setOverrideDialog(null);
      setOverrideNote('');
      // Refresh leaderboard
      const params = { 
        category: activeCategory, 
        limit: PAGE_SIZE,
        skip: (currentPage - 1) * PAGE_SIZE
      };
      const { data } = await getLeaderboardData(params);
      setLeaderboard(data);
    } catch (error) {
      toast.error('Failed to update override');
    }
  };

  const handleRemoveOverride = async (userId) => {
    try {
      await removeLeaderboardOverride(userId, activeCategory);
      toast.success('Override removed');
      // Refresh leaderboard
      const params = { 
        category: activeCategory, 
        limit: PAGE_SIZE,
        skip: (currentPage - 1) * PAGE_SIZE
      };
      const { data } = await getLeaderboardData(params);
      setLeaderboard(data);
    } catch (error) {
      toast.error('Failed to remove override');
    }
  };

  const clearFilters = () => {
    setFilters({
      department_id: '',
      role_id: '',
      location: '',
      call_type: '',
      month: '',
    });
  };

  const hasActiveFilters = Object.values(filters).some(v => v);

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="h-6 w-6 text-yellow-500" />;
    if (rank === 2) return <Medal className="h-6 w-6 text-gray-400" />;
    if (rank === 3) return <Award className="h-6 w-6 text-amber-600" />;
    return null;
  };

  const getRankBg = (rank) => {
    if (rank === 1) return 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200';
    if (rank === 2) return 'bg-gradient-to-r from-gray-50 to-slate-50 border-gray-200';
    if (rank === 3) return 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200';
    return '';
  };

  return (
    <div className="space-y-6" data-testid="admin-leaderboard">
      {/* Header */}
      <div className="flex items-center gap-4 p-6 bg-gradient-to-r from-yellow-500 via-amber-500 to-orange-500 rounded-xl text-white shadow-lg">
        <div className="p-3 bg-white/20 rounded-xl">
          <Trophy className="h-8 w-8" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold font-heading">Leaderboard</h1>
          <p className="text-yellow-100">Top performers across the organization</p>
        </div>
      </div>

      {/* Category Tabs */}
      <Tabs value={activeCategory} onValueChange={setActiveCategory}>
        <TabsList className="flex flex-wrap h-auto gap-2 bg-transparent p-0">
          {CATEGORIES.map((cat) => (
            <TabsTrigger
              key={cat.value}
              value={cat.value}
              className="data-[state=active]:bg-slate-900 data-[state=active]:text-white gap-2"
            >
              <cat.icon className={`h-4 w-4 ${cat.color}`} />
              {cat.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Filters */}
        <Card className="card-elevated mt-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Filter className="h-4 w-4" />
                <span className="text-sm font-medium">Filters:</span>
              </div>
              
              <Select value={filters.department_id || 'all'} onValueChange={(v) => setFilters({...filters, department_id: v === 'all' ? '' : v})}>
                <SelectTrigger className="w-[180px]">
                  <Building2 className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="All Departments" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  {departments.map(dept => (<SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>))}
                </SelectContent>
              </Select>
              
              <Select value={filters.role_id || 'all'} onValueChange={(v) => setFilters({...filters, role_id: v === 'all' ? '' : v})}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="All Roles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  {roles.map(role => (<SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>))}
                </SelectContent>
              </Select>
              
              <Select value={filters.call_type || 'all'} onValueChange={(v) => setFilters({...filters, call_type: v === 'all' ? '' : v})}>
                <SelectTrigger className="w-[150px]">
                  <Phone className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="All Call Types" />
                </SelectTrigger>
                <SelectContent>
                  {CALL_TYPES.map(type => (<SelectItem key={type.value} value={type.value || 'all'}>{type.label}</SelectItem>))}
                </SelectContent>
              </Select>
              
              <Select value={filters.month || 'all'} onValueChange={(v) => setFilters({...filters, month: v === 'all' ? '' : v})}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Time" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  {MONTH_OPTIONS.map(month => (<SelectItem key={month.value} value={month.value}>{month.label}</SelectItem>))}
                </SelectContent>
              </Select>
              
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground">
                  <X className="h-4 w-4 mr-1" /> Clear All
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Leaderboard Content */}
        {CATEGORIES.map((cat) => (
          <TabsContent key={cat.value} value={cat.value} className="mt-6 space-y-6">
            {isLoading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (<Skeleton key={i} className="h-20" />))}
              </div>
            ) : leaderboard ? (
              <>
                {/* Top 3 Podium */}
                {leaderboard.top_3?.length > 0 && (
                  <div className="grid grid-cols-3 gap-4 mb-8">
                    {/* Second Place */}
                    <div className="mt-8">
                      {leaderboard.top_3[1] && (
                        <Card className={`card-elevated ${getRankBg(2)} text-center`}>
                          <CardContent className="pt-6">
                            <div className="relative inline-block">
                              <Avatar className="h-16 w-16 border-4 border-gray-300">
                                <AvatarImage src={getAvatarDisplayUrl(leaderboard.top_3[1])} />
                                <AvatarFallback className="text-lg">{leaderboard.top_3[1].user_name?.charAt(0)}</AvatarFallback>
                              </Avatar>
                              <div className="absolute -top-2 -right-2 bg-gray-400 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">
                                2
                              </div>
                            </div>
                            <h3 className="font-semibold mt-3">{leaderboard.top_3[1].user_name}</h3>
                            <p className="text-3xl font-bold text-slate-900 mt-2">{leaderboard.top_3[1].score}</p>
                            <p className="text-xs text-muted-foreground">{leaderboard.top_3[1].call_count} calls</p>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                    
                    {/* First Place */}
                    <div>
                      {leaderboard.top_3[0] && (
                        <Card className={`card-elevated ${getRankBg(1)} text-center shadow-lg`}>
                          <CardContent className="pt-6">
                            <Crown className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
                            <div className="relative inline-block">
                              <Avatar className="h-20 w-20 border-4 border-yellow-400">
                                <AvatarImage src={getAvatarDisplayUrl(leaderboard.top_3[0])} />
                                <AvatarFallback className="text-xl">{leaderboard.top_3[0].user_name?.charAt(0)}</AvatarFallback>
                              </Avatar>
                              <div className="absolute -top-2 -right-2 bg-yellow-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">
                                1
                              </div>
                            </div>
                            <h3 className="font-semibold text-lg mt-3">{leaderboard.top_3[0].user_name}</h3>
                            <p className="text-4xl font-bold text-slate-900 mt-2">{leaderboard.top_3[0].score}</p>
                            <p className="text-xs text-muted-foreground">{leaderboard.top_3[0].call_count} calls</p>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                    
                    {/* Third Place */}
                    <div className="mt-12">
                      {leaderboard.top_3[2] && (
                        <Card className={`card-elevated ${getRankBg(3)} text-center`}>
                          <CardContent className="pt-6">
                            <div className="relative inline-block">
                              <Avatar className="h-14 w-14 border-4 border-amber-400">
                                <AvatarImage src={getAvatarDisplayUrl(leaderboard.top_3[2])} />
                                <AvatarFallback>{leaderboard.top_3[2].user_name?.charAt(0)}</AvatarFallback>
                              </Avatar>
                              <div className="absolute -top-2 -right-2 bg-amber-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">
                                3
                              </div>
                            </div>
                            <h3 className="font-semibold mt-3">{leaderboard.top_3[2].user_name}</h3>
                            <p className="text-2xl font-bold text-slate-900 mt-2">{leaderboard.top_3[2].score}</p>
                            <p className="text-xs text-muted-foreground">{leaderboard.top_3[2].call_count} calls</p>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  </div>
                )}

                {/* Full Rankings Table */}
                <Card className="card-elevated">
                  <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <cat.icon className={`h-5 w-5 ${cat.color}`} />
                      {cat.label} Rankings
                    </CardTitle>
                    <CardDescription>{leaderboard.total_users} participants</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-16">Rank</TableHead>
                          <TableHead>User</TableHead>
                          <TableHead className="text-right">Score</TableHead>
                          <TableHead className="text-right">Calls</TableHead>
                          <TableHead className="text-right">Hours</TableHead>
                          <TableHead className="text-right">Avg Score</TableHead>
                          <TableHead className="w-20">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {leaderboard.rankings?.map((user) => (
                          <TableRow key={user.user_id} className={`${user.is_admin_override ? 'bg-amber-50' : ''} ${getRankBg(user.rank)}`}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {getRankIcon(user.rank)}
                                <span className={`font-bold ${user.rank <= 3 ? 'text-lg' : ''}`}>#{user.rank}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <Avatar className="h-10 w-10">
                                  <AvatarImage src={getAvatarDisplayUrl(user)} />
                                  <AvatarFallback>{user.user_name?.charAt(0)}</AvatarFallback>
                                </Avatar>
                                <div>
                                  <p className="font-medium">{user.user_name}</p>
                                  {user.is_admin_override && (
                                    <Badge variant="outline" className="text-xs bg-amber-100">
                                      Admin Note: {user.override_note || 'Modified'}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <span className="text-xl font-bold">{user.score}</span>
                            </TableCell>
                            <TableCell className="text-right">{user.call_count}</TableCell>
                            <TableCell className="text-right">{user.total_hours}h</TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Star className="h-4 w-4 text-amber-500" />
                                {user.average_score}
                              </div>
                            </TableCell>
                            <TableCell>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => setOverrideDialog(user)}>
                                    <MessageSquare className="h-4 w-4 mr-2" />
                                    Add Note/Override
                                  </DropdownMenuItem>
                                  {user.is_admin_override && (
                                    <DropdownMenuItem onClick={() => handleRemoveOverride(user.user_id)} className="text-red-600">
                                      <X className="h-4 w-4 mr-2" />
                                      Remove Override
                                    </DropdownMenuItem>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    
                    {/* Pagination */}
                    {leaderboard?.total_pages > 1 && (
                      <div className="flex items-center justify-between px-4 py-3 border-t">
                        <div className="text-sm text-muted-foreground">
                          Showing {((currentPage - 1) * PAGE_SIZE) + 1} - {Math.min(currentPage * PAGE_SIZE, leaderboard.total_users)} of {leaderboard.total_users} users
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                          >
                            Previous
                          </Button>
                          <span className="text-sm">
                            Page {currentPage} of {leaderboard.total_pages}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(p => Math.min(leaderboard.total_pages, p + 1))}
                            disabled={currentPage === leaderboard.total_pages}
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="card-elevated">
                <CardContent className="py-12 text-center">
                  <Trophy className="h-16 w-16 text-muted-foreground/70 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-700 mb-2">No Rankings Yet</h3>
                  <p className="text-muted-foreground">Complete some calls to appear on the leaderboard.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Override Dialog */}
      <Dialog open={!!overrideDialog} onOpenChange={() => { setOverrideDialog(null); setOverrideNote(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Leaderboard Override</DialogTitle>
            <DialogDescription>
              Add a note or modify {overrideDialog?.user_name}&apos;s leaderboard status for {activeCategory.replace(/_/g, ' ')} category.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Note (optional)</label>
              <Input
                value={overrideNote}
                onChange={(e) => setOverrideNote(e.target.value)}
                placeholder="e.g., Score adjusted due to data issue"
                className="mt-1"
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setOverrideDialog(null); setOverrideNote(''); }}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => handleOverride('exclude')}>
              <Ban className="h-4 w-4 mr-2" />
              Exclude from Ranking
            </Button>
            <Button onClick={() => handleOverride('note')}>
              <MessageSquare className="h-4 w-4 mr-2" />
              Add Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
